<template>
	<modal name="search-local" @opened="opened" @closed="closed" :pivot-y="0.2" :width="modalWidth" :height="modalHeight">
		<div id="search-local-modal">
			<input ref="localSearchInput"
				   type="text"
				   name="search-local-input"
				   :placeholder="modalWidth >= 400 ? $t('localSearch.placeholderInputNameLonger') : $t('localSearch.placeholderInputNameShorter')"
				   v-model="query"
				   autocomplete="off"
				   @keyup="search">
			<div id="search-local-list">
				<div class="item item-author" v-for="author in authors" v-bind:key="author.id" :class="{ active: currentItem !== null && currentItem.id === author.id }">
					<div class="left" @click="selectAuthor(author)">
						<div class="title">{{ author.title }}</div>
					</div>
					<div class="controllers">
					</div>
				</div>
				<div class="item item-song" v-for="song in songs" v-bind:key="song.id" :class="{ active: currentItem !== null && currentItem.id === song.id }">
					<div class="left" @click="selectSong(song)">
						<div class="title">{{ song.title }}</div>
						<div class="author">{{ song.author }}</div>
					</div>
					<div class="controllers">
						<button type="button" class="add-to-queue bigger" @click="addSongToQueue(song)" :disabled="isSongInQueue(song)">{{ $t('localSearch.queueButton') }}</button>
						<button type="button" class="add-to-queue smaller" @click="addSongToQueue(song)" :disabled="isSongInQueue(song)"><IconPlus/></button>
					</div>
				</div>
				<template v-if="externalSongs.length > 0">
					<div class="external-songs-info">
						<hr />
						můžete si přidat do svého zpěvníku:
					</div>
					<div class="item item-song"
						 v-for="song in externalSongs"
						 v-bind:key="song.id"
						 :class="{ active: currentItem !== null && currentItem.id === song.id }">
						<div class="left" @click="selectExternalSong(song)" style="width: 100%;">
							<div class="title">{{ song.title }} <span v-if="song.isChecked">👍&nbsp;</span></div>
							<div class="author">{{ song.author }}</div>
						</div>
					</div>
				</template>
			</div>
		</div>
	</modal>
</template>

<script>
	import Vue from "vue";
	import Fuse from "fuse.js";
	import { mapState } from "vuex";
	import { SongService } from "@/services/SongService";
	import IconPlus from "@/components/icons/IconPlus";
	import diacritics from "diacritics";
	import {ExternalSearchService} from "@/services/ExternalSearchService";

	let controller = new AbortController

	export default {
		components: {IconPlus},
		data() {
			return {
				query: "",
				authors: [],
				songs: [],
				externalSongs: [],
				currentItem: null,
				currentItemIndex: null
			}
		},
		computed: {
			...mapState([ "queueSongs", "localSearchOpened", "device" ]),
			modalWidth() {
				let width = 450;
				while (width > window.innerWidth) width -= 50;
				return width;
			},
			modalHeight() {
				let height = window.innerHeight - 300;
				if (height < 400) height = 400;
				return height;
			}
		},
		methods: {
			cancelSearchRequest () {
				controller.abort()
				controller = new AbortController
			},
			opened() {
				document.addEventListener("keyup", this.keydownEvent);
				this.$store.commit("setLocalSearchOpened", true);
				Vue.nextTick(() => {
					if (this.$refs.localSearchInput) {
						if (this.device && this.device.platform !== "web") {
							this.query = "";
						}
						this.$refs.localSearchInput.focus();
						this.$refs.localSearchInput.select();
					}
				});
			},
			closed() {
				document.removeEventListener("keyup", this.keydownEvent);
				this.$store.commit("setLocalSearchOpened", false);
			},
			addSongToQueue(song) {
				SongService.addSongToQueue(song);
			},
			selectSong(song) {
				// TODO: Tady pripravit i relevantni filtr
				this.$store.commit("setRootSideOpened", false);
				this.$store.commit("setExternalSearchOpened", false);
				this.$store.commit("setCurrentSong", song);
				this.$router.push({ name: "zpevnik-song", params: { song: song.songId } });
				this.$modal.hide("search-local");
			},
			selectExternalSong(song) {
				this.$store.commit("setRootSideOpened", false);
				this.$store.commit("setExternalSearchOpened", true);
				this.$store.commit("setCurrentSong", song);
				this.$router.push({ name: "zpevnik-song", params: { song: song.id } });
				this.$modal.hide("search-local");
				this.$nextTick(() => this.$store.commit("setExternalSearchQuery", this.query))
			},
			selectAuthor(author) {
				this.$store.commit("setRootSideOpened", false);
				this.$store.commit("setExternalSearchOpened", false);
				this.$store.commit("setSongsFilter", {
					playlist: null,
					author: author.id,
					onlyQueue: false
				});
				this.$modal.hide("search-local");
			},
			search(e) {
				if (e.code === "ArrowDown" || e.code === "ArrowUp") return;

				this.currentItemIndex = null;
				this.currentItem = null;
				this.authors = [];
				this.songs = [];
				this.externalSongs = [];
				const query = diacritics.remove(this.query).trim().split(" ");

				let sourceOfSongs = Object.values(this.$store.state.songs).map(s => {
					const normalizedTitle = diacritics.remove(s.title);
					const normalizedAuthor = diacritics.remove(s.author);
					const normalizedContent = normalizedAuthor + " " + normalizedTitle + " " + diacritics.remove(s.content
						.replace(/\[([^\]]+)]/g, ""));

					return {
						...s,
						normalizedTitle: normalizedTitle,
						normalizedAuthor: normalizedAuthor,
						normalizedContent: normalizedContent,
					}
				});

				let sourceOfAuthors = Object.values(this.$store.state.authors).map(a => {
					return {
						...a,
						normalizedTitle: diacritics.remove(a.title)
					}
				});

				var songFuse = new Fuse(sourceOfSongs, {
					shouldSort: true,
					threshold: 0.25,
					ignoreLocation: true,
					maxPatternLength: 32,
					minMatchCharLength: 1,
					keys: [
						"normalizedTitle",
						"normalizedAuthor",
						"normalizedContent"
					]
				});

				var authorFuse = new Fuse(sourceOfAuthors, {
					shouldSort: true,
					threshold: 0.1,
					ignoreLocation: true,
					maxPatternLength: 32,
					minMatchCharLength: 1,
					keys: [
						"normalizedTitle"
					]
				});

				this.cancelSearchRequest();
				ExternalSearchService.search({
					query: this.query,
					limit: 20,
					signal: controller.signal
				}).then(items => this.externalSongs = items)

				if (query.join(" ").length > 2) {
					this.songs = songFuse.search({
						$or: [
							{
								$and: query.map(q => {
									return {
										"normalizedTitle": q
									}
								}),
							},
							{
								$and: query.map(q => {
									return {
										"normalizedContent": q
									}
								})
							}
						]
					}).map(r => r.item);

					this.authors = authorFuse.search(query.join(" ")).map(r => r.item);
				}
			},
			keydownEvent(e) {
				if (!this.localSearchOpened) return;
				if (e.code === "Enter") {
					if (this.currentItem === null) return;
					if ("songId" in this.currentItem) {
						this.selectSong(this.currentItem);
					} else {
						this.selectAuthor(this.currentItem);
					}
				}
				if (e.code === "KeyF" || e.code === "Backspace") {
					Vue.nextTick(() => {
						this.$refs.localSearchInput.focus()
					});
				}
				if (this.authors.length === 0 && this.songs.length === 0) return;
				if (!(e.code === "ArrowDown" || e.code === "ArrowUp")) return;

				Vue.nextTick(() => {
					this.$refs.localSearchInput.blur();
				});

				if (e.code === "Escape") {
					this.$modal.hide("search-local");
					return;
				}

				if (e.code === "ArrowDown") {
					if (this.currentItemIndex === null) {
						this.currentItemIndex = -1;
					}
					this.currentItemIndex += 1;
				} else if (e.code === "ArrowUp") {
					if (this.currentItemIndex === null || this.currentItemIndex <= 0) {
						return;
					}

					this.currentItemIndex -= 1;
				}

				if (this.currentItemIndex + 1 <= this.authors.length) {
					this.currentItem = this.authors[this.currentItemIndex];
				} else if (this.currentItemIndex + 1 <= this.authors.length + this.songs.length) {
					this.currentItem = this.songs[this.currentItemIndex - this.authors.length];
				} else {
					this.currentItemIndex -= 1;
				}
			},
			isSongInQueue(song) {
				return this.queueSongs.includes(song.songId);
			}
		},
	}
</script>

<style lang="scss" scoped>
	#search-local-modal {
		width: 100%;
		height: 100%;
		background-color: #264653;
		color: rgba(255, 255, 255, 0.8);
		font-size: 1.1em;

		input {
			width: 100%;
			height: 50px;
			padding: 0 20px;
			line-height: 50px;
			background-color: rgba(255, 255, 255, 0.21);
			font-weight: 300;
			color: rgba(255, 255, 255, 0.8);
			border: 0;

			&::placeholder {
				color: rgba(255, 255, 255, 0.49);
			}

			&:focus {
				outline: none;
			}
		}

		#search-local-list {
			height: 87%;
			overflow-y: scroll;
			overflow-x: hidden;
			margin: 5px 0 0 0;

			&::-webkit-scrollbar {
				width: 8px;
			}

			&::-webkit-scrollbar-track {
				-webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
				background-color: rgba(59, 115, 135, 0.26);
			}

			&::-webkit-scrollbar-thumb {
				background-color: rgba(42, 157, 143, 0.7);
			}

			.item {
				padding: 14px 20px;
				cursor: pointer;
				width: 100%;
				height: 38px;

				&:hover {
					background-color: rgba(255, 255, 255, 0.21);
				}

				&.active {
					background-color: rgba(255, 255, 255, 0.21);
				}

				.left {
					width: 65%;
					float: left;
				}

				.controllers {
					width: 35%;
					float: right;
				}
			}

			.item-author {
				.title {
					margin-top: 8px;
				}
			}

			.item-song {
				.title {
				}

				.author {
					opacity: 0.5;
				}
			}

			.external-songs-info {
				opacity: 0.6;
				font-size: 12px;
				text-align: center;
				margin: 20px 0;

				hr {
					margin: 35px auto 10px auto;
					box-sizing: content-box;
					height: 0;
					overflow: visible;
					width: 100px;
					border: none;
					border-top: 1px solid rgba(255, 255, 255, 0.8);
				}
			}

			button.add-to-queue {
				font-weight: 300;
				padding: 0 15px;
				border: 0;
				font-size: 1em;
				line-height: 40px;
				color: rgba(255, 255, 255, 0.75);
				cursor: pointer;
				margin-left: 10px;
				border-radius: 4px;
				float: left;
				margin-right: 10px;
				background-color: #ef904e;

				&:disabled {
					opacity: 0.5;
					cursor: not-allowed;
				}

				.icon {
					fill: white;
					margin: 9px 0 0 0;
				}

				&:hover {
					background-color: #d07a46;
				}

				&:focus {
					outline: none;
				}

				@media only screen and (max-width: 400px) {
					&.bigger { display: none; }
					&.smaller { display: block; }
				}

				@media only screen and (min-width: 400px) {
					&.bigger { display: block; }
					&.smaller { display: none; }
				}
			}

		}

	}
</style>
